import React, { useEffect, useRef } from 'react';
import FeatherIcon from './FeatherIcon';
import '../styles/money-flow.css';

interface Step {
  readonly icon: string;
  readonly tone: 'give' | 'money' | 'care';
  readonly title: string;
  readonly sub: string;
}

// The path a dollar actually travels, at a glance. Order is the sequence, left to right.
const STEPS: readonly Step[] = [
  { icon: 'gift', tone: 'give', title: 'Donation in', sub: 'a real, signed gift' },
  { icon: 'pie-chart', tone: 'give', title: '70 / 30 split', sub: 'care pool · operations' },
  { icon: 'tag', tone: 'money', title: 'Capped voucher', sub: 'up to the procedure cap' },
  { icon: 'heart', tone: 'care', title: 'Clinic delivers care', sub: 'voucher locked to the clinic' },
  { icon: 'credit-card', tone: 'money', title: 'Clinic paid — capped', sub: 'the lesser of billed vs cap' }
];

const HowItWorks: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = ref.current?.querySelectorAll('.mf-reveal');
    if (!els) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((e) => e.classList.add('mf-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('mf-in');
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="mf-money py-20 bg-paper" ref={ref}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-10">
          <p className="mf-eyebrow">How the money moves</p>
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Where your donation goes</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Donations become capped vouchers. Clinics are paid only for the care they deliver &mdash;
            and whatever a procedure doesn&rsquo;t spend flows back to the next animal.
          </p>
        </div>

        <ol className="mf-flow">
          {STEPS.map((step, i) => (
            <li
              key={step.title}
              className="mf-step mf-reveal"
              data-tone={step.tone}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <span className="mf-node" aria-hidden="true">
                <FeatherIcon name={step.icon} />
              </span>
              <span className="mf-step-txt">
                <span className="mf-label">{step.title}</span>
                <span className="mf-sub">{step.sub}</span>
              </span>
            </li>
          ))}
        </ol>

        <div className="mf-rule mf-reveal">
          <span className="mf-rule-ico" aria-hidden="true">
            <FeatherIcon name="repeat" />
          </span>
          <p>
            Clinics are paid the <b>lesser</b> of care billed and the voucher cap. On a $600 voucher
            where the clinic bills $450, the clinic gets $450 &mdash; and the remaining $150 loops back
            to the pool, funding the next animal instead of padding an invoice.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
